import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search as SearchIcon, Filter, RefreshCcw, CheckCircle2, Music, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function fmtDuration(seconds) {
  if (!seconds && seconds !== 0) return "";
  const m = Math.floor(seconds / 60); const s = Math.round(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function BetaCatalog() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ genres: [], moods: [], uses: [] });
  const [loading, setLoading] = useState(true);
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [genre, setGenre] = useState("all");
  const [mood, setMood] = useState("all");
  const [useTag, setUseTag] = useState("all");
  const [clean, setClean] = useState("all");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (genre !== "all") params.genre = genre;
      if (mood !== "all") params.mood = mood;
      if (useTag !== "all") params.use = useTag;
      if (clean === "clean") params.clean = true;
      if (clean === "explicit") params.clean = false;
      const r = await api.get("/beta/catalog", { params });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(errMsg(e, "Could not load catalog."));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [q, genre, mood, useTag, clean]);

  useEffect(() => {
    refresh();
    api.get("/beta/meta")
      .then((r) => setMeta(r.data || { genres: [], moods: [], uses: [] }))
      .catch(() => {});
  }, [refresh]);

  const headlineTotal = useMemo(() => rows.length, [rows]);

  return (
    <div className="space-y-6" data-testid="beta-catalog">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">
            Industry Beta · Catalog Preview
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight flex items-center gap-3">
            <Sparkles className="w-7 h-7" style={{ color: "hsl(347 60% 22%)" }} />
            Beta Catalog
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Selected works approved for industry beta evaluation. Use the catalog the way you would in your normal
            sync / supervision workflow, then share feedback against a specific work or the experience overall.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild size="sm" data-testid="beta-catalog-feedback-cta">
            <Link to="/beta/feedback">Submit general feedback</Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => { e.preventDefault(); setQ(qInput.trim()); }}
            data-testid="beta-catalog-filters"
          >
            <div className="flex-1 min-w-[240px]">
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Search</label>
              <div className="flex items-center gap-2">
                <SearchIcon className="w-3.5 h-3.5 text-muted-foreground" />
                <Input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Title, artist, genre\u2026" data-testid="beta-search" />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Genre</label>
              <Select value={genre} onValueChange={setGenre}>
                <SelectTrigger className="w-[180px]" data-testid="beta-filter-genre">
                  <Filter className="w-3.5 h-3.5 mr-1.5" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any genre</SelectItem>
                  {(meta.genres || []).map((g) => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Mood</label>
              <Select value={mood} onValueChange={setMood}>
                <SelectTrigger className="w-[180px]" data-testid="beta-filter-mood"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any mood</SelectItem>
                  {(meta.moods || []).map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Best use</label>
              <Select value={useTag} onValueChange={setUseTag}>
                <SelectTrigger className="w-[180px]" data-testid="beta-filter-use"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any use</SelectItem>
                  {(meta.uses || []).map((u) => (
                    <SelectItem key={u} value={u}>{u}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Clean / Explicit</label>
              <Select value={clean} onValueChange={setClean}>
                <SelectTrigger className="w-[160px]" data-testid="beta-filter-clean"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  <SelectItem value="clean">Clean</SelectItem>
                  <SelectItem value="explicit">Explicit only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" size="sm" data-testid="beta-apply">Apply</Button>
            <Button
              type="button" size="sm" variant="ghost"
              onClick={() => { setQInput(""); setQ(""); setGenre("all"); setMood("all"); setUseTag("all"); setClean("all"); }}
              data-testid="beta-clear"
            >
              Clear
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={refresh} data-testid="beta-refresh">
              <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
            <div className="text-[11.5px] text-muted-foreground ml-auto" data-testid="beta-result-count">
              {headlineTotal} {headlineTotal === 1 ? "work" : "works"}
            </div>
          </form>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-sm text-muted-foreground" data-testid="beta-loading">Loading\u2026</div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="text-center text-sm text-muted-foreground py-14" data-testid="beta-empty">
            No works in the beta catalog right now. Check back soon — we add works on a rolling basis.
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="beta-grid">
          {rows.map((t) => (
            <Card key={t.id} className="flex flex-col" data-testid={`beta-card-${t.id}`}>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Music className="w-4 h-4 opacity-70" />
                  <span className="truncate" title={t.track_title}>{t.track_title}</span>
                </CardTitle>
                <CardDescription className="truncate" title={t.artist || ""}>
                  {t.artist || "\u2014"}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1 space-y-2 text-[13px]">
                <div className="flex flex-wrap items-center gap-1.5">
                  {t.genre && <Badge variant="outline">{t.genre}</Badge>}
                  {t.sub_genre && <Badge variant="outline" className="opacity-80">{t.sub_genre}</Badge>}
                  {(t.mood_tags || []).slice(0, 3).map((m) => (
                    <Badge key={m} variant="secondary">{m}</Badge>
                  ))}
                </div>
                <div className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-[12px]">
                  {t.bpm ? <span>BPM {t.bpm}</span> : null}
                  {t.duration ? <span>{fmtDuration(t.duration)}</span> : null}
                  {t.instrumental_available ? <span>Instrumental available</span> : null}
                  {t.clean_available ? <span>Clean version</span> : null}
                </div>
                {t.short_description && (
                  <p className="text-[12.5px] text-slate-700 line-clamp-3">{t.short_description}</p>
                )}
                <div className="pt-1 flex items-center justify-between">
                  <Badge
                    variant="outline"
                    className="text-[10px] uppercase tracking-wide"
                    style={{
                      background: t.clearance_readiness === "Ready for industry review" ? "hsl(135 35% 90%)" : "hsl(40 60% 92%)",
                      color: t.clearance_readiness === "Ready for industry review" ? "hsl(135 35% 22%)" : "hsl(40 60% 24%)",
                    }}
                    data-testid={`beta-clearance-${t.id}`}
                  >
                    <CheckCircle2 className="w-3 h-3 mr-1" /> {t.clearance_readiness}
                  </Badge>
                </div>
              </CardContent>
              <div className="px-6 pb-4 flex justify-end gap-2">
                <Button asChild size="sm" variant="outline" data-testid={`beta-card-feedback-${t.id}`}>
                  <Link to={`/beta/feedback?track=${t.id}`}>Give feedback</Link>
                </Button>
                <Button asChild size="sm" data-testid={`beta-card-open-${t.id}`}>
                  <Link to={`/beta/track/${t.id}`}>Open</Link>
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
